import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";

import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Loader from "../../components/Loader/Loader";
import BASE_URL from "../../config";
import { useAuth } from "../../context/AuthContext";
import { formatDeliveryWindow } from "../../utils/checkoutTotals";
import "./OrderConfirmation.css";

export default function OrderConfirmation() {
  const { isLoggedIn } = useAuth();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const status = searchParams.get("status");
  const method = searchParams.get("method");

  const [orderInfo, setOrderInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTracking = async () => {
      if (!orderId) return;
      setLoading(true);
      try {
        const response = await fetch(`${BASE_URL}/orders/${orderId}/tracking`);
        const result = await response.json();
        if (result?.success) {
          setOrderInfo(result.data);
        }
      } catch (err) {
        console.error("Order confirmation: failed to load order info", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTracking();
  }, [orderId]);

  const isSuccess = status === "success";
  const isCod = method === "cod";
  const isCodAdvance = method === "cod-advance";

  const deliveryBy = orderInfo ? formatDeliveryWindow(orderInfo.estimatedDeliveryFrom, orderInfo.estimatedDeliveryTo) : "";
  // A COD order waits for the customer's one-click confirmation (the link we send to their phone / email)
  const awaitingCodConfirmation = Boolean(isSuccess && orderInfo && orderInfo.paymentMethod === "cod" && !orderInfo.codConfirmed);
  const money = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

  return (
    <>
      <Header />

      <div className="confirmation-page">
        <div className="confirmation-card">
          {isSuccess ? (
            <FaCheckCircle className="confirmation-icon success" />
          ) : (
            <FaTimesCircle className="confirmation-icon failed" />
          )}

          <h1>
            {isSuccess
              ? isCod
                ? "Order Placed Successfully!"
                : isCodAdvance
                ? "Order Confirmed!"
                : "Payment Successful!"
              : "Payment Failed"}
          </h1>

          <p className="confirmation-message">
            {isSuccess
              ? isCod
                ? "Your order has been placed and will be delivered soon. You'll pay in cash on delivery."
                : isCodAdvance
                ? "Your advance payment was received and your order is confirmed. Please pay the remaining amount in cash on delivery."
                : "Your payment was received and your order is confirmed."
              : "We couldn't process your payment. No amount has been charged, or it will be refunded automatically if deducted."}
          </p>

          {loading ? (
            <Loader label="Loading order details..." />
          ) : orderInfo ? (
            <div className="confirmation-order-details">
              <div>
                <span>Order Number</span>
                <strong>#{orderInfo.orderNumber}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{orderInfo.currentStatus}</strong>
              </div>
              {deliveryBy && (
                <div>
                  <span>Delivery</span>
                  <strong>{deliveryBy.replace("Delivery by ", "By ")}</strong>
                </div>
              )}
              {Number(orderInfo.advanceAmount) > 0 && (
                <>
                  <div>
                    <span>Paid online now</span>
                    <strong>{money(orderInfo.advanceAmount)}</strong>
                  </div>
                  <div>
                    <span>To pay on delivery</span>
                    <strong>{money(orderInfo.total - orderInfo.advanceAmount)}</strong>
                  </div>
                </>
              )}
              {Number(orderInfo.prepaidDiscount) > 0 && (
                <div>
                  <span>You saved by paying online</span>
                  <strong>{money(orderInfo.prepaidDiscount)}</strong>
                </div>
              )}
            </div>
          ) : orderId ? (
            <p className="confirmation-order-id">Order ID: {orderId}</p>
          ) : null}

          {awaitingCodConfirmation && (
            <p className="confirmation-message confirmation-confirm-note" role="status">
              <strong>One more step:</strong> we have sent you a confirmation link by email (and WhatsApp / SMS when it
              is available). Please tap <strong>Confirm</strong> so we can dispatch your order.
            </p>
          )}

          {isSuccess && (
            <p className="confirmation-message">
              {isLoggedIn
                ? "Changed your mind or entered something wrong? Until your order is dispatched you can cancel it, edit the delivery address or reschedule delivery from My Orders."
                : "Changed your mind or entered something wrong? Contact us before the order is dispatched and we will change or cancel it for you."}
            </p>
          )}

          <div className="confirmation-actions">
            {isSuccess ? (
              <Link to="/account" state={{ activeTab: "orders" }} className="confirmation-btn primary">
                View My Orders
              </Link>
            ) : (
              <Link to="/" className="confirmation-btn primary">
                Try Again from Shop
              </Link>
            )}

            <Link to="/" className="confirmation-btn secondary">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}
